import React, { useContext } from 'react';

import { useLayoutContext } from '@contexts/LayoutContext';
import { useUserPermission } from '@contexts/UserContext';

import { NavMenu, NwNavIcon } from '@components/_LayoutComponents/Header/NwHeaderUI';

import { getNavClassName, getNavMenuMode } from './NwNavUtils';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const NwNavBarBooking = ({
    allLogs,
    createNewItemMenu,
    current,
    drawer,
    handleChangeOpenKeys,
    handleClick,
    isLoaded,
    isOpen,
    lastSeenMenu,
    openKeys,
}) => {
    const layoutContext = useLayoutContext();
    const { breakpoints } = layoutContext;
    const ViewModelLegals = useUserPermission('ViewModelLegals');
    const canAccessModelDataUpdates = useUserPermission('DataUpdateRequest');
    const { modelLogs, customerLogs, projectLogs } = allLogs;

    const navItems = [
        { key: '/dashboard', label: 'Dashboard', disabled: !isLoaded },
        {
            key: '/menu-models',
            label: 'Models',
            popupClassName: !isOpen('/menu-models') ? 'main-nav-submenu menu-disabled' : 'main-nav-submenu menu-double',
            disabled: !isLoaded,
            onTitleClick: () => handleClick({ key: '/models' }),
            children: [
                lastSeenMenu(modelLogs, 'model', 'no models'),
                {
                    type: 'group',
                    key: `/menu-tools-models`,
                    label: (
                        <span className="submenu-title-wrapper">
                            <NwNavIcon icon={byPrefixAndName.fasl['bolt']} />
                            Tools
                        </span>
                    ),
                    children: [
                        {
                            key: '/models',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['radar']} />
                                    Browse Models
                                </div>
                            ),
                        },
                        {
                            key: '/models-archive',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['archive']} />
                                    Models Archive
                                </div>
                            ),
                        },
                        {
                            key: '/models-legal',
                            disabled: !ViewModelLegals,
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['passport']} />
                                    Models Documents Search
                                </div>
                            ),
                        },
                        {
                            key: '/rooms/chart',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['house-user']} />
                                    Rooms Management
                                </div>
                            ),
                        },
                        {
                            key: '/model-data-updates',
                            disabled: !canAccessModelDataUpdates,
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['envelope']} />
                                    Model Data Updates
                                </div>
                            ),
                        },
                    ],
                },
                createNewItemMenu('Model'),
            ],
        },
        {
            key: '/menu-schedule',
            label: 'Bookings',
            popupClassName: !isOpen('/menu-schedule')
                ? 'main-nav-submenu menu-disabled'
                : 'main-nav-submenu menu-double',
            disabled: !isLoaded,
            onTitleClick: () => handleClick({ key: '/schedule' }),
            children: [
                lastSeenMenu(projectLogs, 'bookings', 'no projects', 'customerName'),
                {
                    type: 'group',
                    key: `/menu-tools-projects`,
                    label: (
                        <span className="submenu-title-wrapper">
                            <NwNavIcon icon={byPrefixAndName.fasl['bolt']} />
                            Tools
                        </span>
                    ),
                    children: [
                        {
                            key: '/schedule',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['calendar']} />
                                    Schedule
                                </div>
                            ),
                        },
                        {
                            key: '/events-search',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['business-time']} />
                                    Events Search
                                </div>
                            ),
                        },
                        {
                            key: '/events-report',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['file-spreadsheet']} />
                                    Events Report
                                </div>
                            ),
                        },
                        {
                            key: '/usages-search',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['handshake']} />
                                    Usages Search
                                </div>
                            ),
                        },
                        {
                            key: '/projects-drafts',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['folders']} />
                                    Projects Drafts
                                </div>
                            ),
                        },
                        {
                            key: '/show-seasons',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['calendar-star']} />
                                    Show Seasons
                                </div>
                            ),
                        },
                    ],
                },
                createNewItemMenu('Project'),
            ],
        },
        {
            key: '/menu-contacts',
            label: 'Contacts',
            popupClassName: !isOpen('/menu-contacts')
                ? 'main-nav-submenu menu-disabled'
                : 'main-nav-submenu menu-double',
            disabled: !isLoaded,
            onTitleClick: () => handleClick({ key: '/customers' }),
            children: [
                lastSeenMenu(customerLogs, 'customer', 'no contacts'),
                {
                    type: 'group',
                    key: `/menu-tools-contacts`,
                    label: (
                        <span className="submenu-title-wrapper">
                            <NwNavIcon icon={byPrefixAndName.fasl['bolt']} />
                            Tools
                        </span>
                    ),
                    children: [
                        {
                            key: '/customers',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['radar']} />
                                    Browse Contacts
                                </div>
                            ),
                        },
                        {
                            key: '/customers-archive',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['archive']} />
                                    Customers Archive
                                </div>
                            ),
                        },
                        {
                            key: '/mailing-lists',
                            label: (
                                <div className="nw-main-nav-subitems">
                                    <NwNavIcon icon={byPrefixAndName.fasl['mailbox']} />
                                    Mailing List
                                </div>
                            ),
                        },
                    ],
                },
                createNewItemMenu('Customer'),
            ],
        },
    ];

    const keysWithSubmenus = navItems.filter((item) => item.children).map((item) => item.key);

    return (
        <NavMenu
            className={getNavClassName(breakpoints, navItems.length)}
            inlineIndent={10}
            items={navItems}
            mode={getNavMenuMode(drawer, breakpoints)}
            onClick={handleClick}
            onOpenChange={(k) => handleChangeOpenKeys(k, keysWithSubmenus)}
            openKeys={openKeys}
            selectedKeys={[current]}
        />
    );
};

export default NwNavBarBooking;
